// src/modules/bookLocation/keyHandover/pages/existingSubscription/SubscriptionManagePage.tsx

import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { atStation, zoneOfLocation } from "../../../../../util/stationTime";
import { CheckCircle2, Loader2 } from "lucide-react";
import type { RootState } from "../../../../../redux/store";
import routes from "../../../../../routes/routes";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../../components/ui/spinner";
import { Button } from "../../../../../components/ui/button";
import {
    useCancelSubscriptionMutation,
    useGetCustomerAgreementsByIdsQuery,
} from "../../../../../api/customerAgreements/customerAgreementsApiSlice";
import SubscriptionSummaryCard from "../../../common/components/SubscriptionSummary/SubscriptionSummaryCard";
import { getSubscriptionActions } from "../../../../../util/subscriptionActions";
import {
    classifySubscriptionLoadError,
    manageIntentSearch,
    serverMessageOf,
    withdrawErrorMessage,
    SubscriptionLoadFailure,
} from "../../common/subscriptionManage";
import type { SubscriptionSummary } from "../../../../../types/bookingCreation";

type Outcome = { kind: "cancelled"; endDate: string } | { kind: "withdrawn" };

const loginTarget = {
    pathname: routes.keyHandoverSubscriptionLoginPath,
    search: manageIntentSearch,
};

function LoadFailure({
    failure,
    status,
    serverMessage,
    onRetry,
}: {
    failure: SubscriptionLoadFailure;
    status?: number;
    serverMessage?: string;
    onRetry: () => void;
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <div className="flex flex-col gap-4 max-w-lg mx-auto text-center" role="alert">
            <p className="text-sm text-red-600">
                {failure === "not_found" && t("subscriptions.manage.error_not_found")}
                {failure === "network" && t("subscriptions.manage.error_network")}
                {failure === "server" &&
                    t("subscriptions.manage.error_server", {
                        status: status ?? t("common.missing"),
                    })}
            </p>
            {serverMessage && failure !== "not_found" && (
                <p className="text-sm text-foreground/70">{serverMessage}</p>
            )}
            {failure === "not_found" ? (
                <Button variant="outline" onClick={() => navigate(loginTarget)}>
                    {t("subscriptions.manage.login_again")}
                </Button>
            ) : (
                <Button variant="outline" onClick={onRetry}>
                    {t("subscriptions.manage.retry")}
                </Button>
            )}
        </div>
    );
}

function ManageContent({ subscriptionId }: { subscriptionId: string }) {
    const { t } = useTranslation();
    const { data, error, isLoading, isFetching, refetch } =
        useGetCustomerAgreementsByIdsQuery(subscriptionId);
    const [cancelSubscription, { isLoading: isSubmitting }] = useCancelSubscriptionMutation();

    const [outcome, setOutcome] = useState<Outcome | null>(null);
    const [confirmingWithdrawal, setConfirmingWithdrawal] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    // On the subscription location's clock (#3219), as OrdersPage shows the
    // same dates — never the phone's: east of the station a deadline at 24:00
    // there would read as the next day here, a day later than the server
    // enforces.
    const formatDate = (value: string | null | undefined, timeZone: string): string =>
        value ? atStation(value, timeZone).format("L") : t("common.missing");

    if (outcome) {
        return (
            <div className="flex flex-col items-center gap-4 max-w-lg mx-auto text-center" role="status">
                <CheckCircle2 className="h-12 w-12 text-primary" />
                <p className="text-base text-foreground">
                    {outcome.kind === "cancelled"
                        ? t("subscriptions.manage.cancelled_success", { date: outcome.endDate })
                        : t("subscriptions.withdraw_dialog.success_message")}
                </p>
            </div>
        );
    }

    if (isLoading || (isFetching && !data)) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    if (error) {
        const status = (error as { status?: unknown }).status;
        return (
            <LoadFailure
                failure={classifySubscriptionLoadError(error)}
                status={typeof status === "number" ? status : undefined}
                serverMessage={serverMessageOf(error)}
                onRetry={refetch}
            />
        );
    }

    // The GET answers an unknown id with an empty list, not a 404.
    const subscription: SubscriptionSummary | undefined = Array.isArray(data)
        ? data.find((item) => item.id === subscriptionId)
        : undefined;
    if (!subscription) {
        return <LoadFailure failure="not_found" onRetry={refetch} />;
    }

    const actions = getSubscriptionActions(subscription);
    const stationZone = zoneOfLocation(subscription.commercialModel?.location);
    const endDate = formatDate(subscription.expiryDate, stationZone);
    const subscriptionName = `${t("subscriptions.ticket_overview.locker")} ${
        subscription.doormappings?.[0]?.door ?? t("common.missing")
    }`;

    const handleCancel = async () => {
        setActionError(null);
        try {
            await cancelSubscription({ id: subscription.id, cancelAtPeriodEnd: true }).unwrap();
            setOutcome({ kind: "cancelled", endDate });
        } catch (cancelError: unknown) {
            setActionError(
                serverMessageOf(cancelError) ?? t("subscriptions.cancel_dialog.error_message")
            );
        }
    };

    const handleWithdraw = async () => {
        setActionError(null);
        try {
            await cancelSubscription({ id: subscription.id, withdraw: true }).unwrap();
            setOutcome({ kind: "withdrawn" });
        } catch (withdrawError: unknown) {
            setActionError(withdrawErrorMessage(withdrawError, t));
        }
    };

    if (confirmingWithdrawal) {
        return (
            <div className="flex flex-col gap-6 max-w-lg mx-auto">
                <h2 className="text-xl font-semibold text-foreground">
                    {t("subscriptions.withdraw_dialog.title")}
                </h2>
                <p className="text-sm text-foreground whitespace-pre-line">
                    {t("subscriptions.withdraw_dialog.content", { subscriptionName })}
                </p>
                {actionError && (
                    <p className="text-sm text-red-600" role="alert">{actionError}</p>
                )}
                <div className="flex flex-col gap-3">
                    <Button size="lg" variant="primary" disabled={isSubmitting} onClick={handleWithdraw}>
                        {isSubmitting && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                        {t("subscriptions.withdraw_dialog.confirm_button")}
                    </Button>
                    <Button
                        variant="ghost"
                        disabled={isSubmitting}
                        onClick={() => {
                            setActionError(null);
                            setConfirmingWithdrawal(false);
                        }}
                    >
                        {t("general.back")}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 max-w-lg mx-auto">
            <SubscriptionSummaryCard subscriptionSummaryData={subscription} />

            {actions.canCancel && (
                <div className="flex flex-col gap-3">
                    <p className="text-sm text-foreground">
                        {t("subscriptions.manage.cancel_hint", { date: endDate })}
                    </p>
                    <Button size="lg" variant="primary" disabled={isSubmitting} onClick={handleCancel}>
                        {isSubmitting && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                        {t("subscriptions.cancel_dialog.confirm_button")}
                    </Button>
                </div>
            )}
            {actions.isScheduledToEnd && (
                <p className="text-sm text-foreground" role="status">
                    {t("subscriptions.manage.already_scheduled", { date: endDate })}
                </p>
            )}
            {actions.isFree && (
                <p className="text-sm text-foreground" role="status">
                    {t("subscriptions.manage.free")}
                </p>
            )}
            {actions.isInactive && !actions.canWithdraw && (
                <p className="text-sm text-foreground" role="status">
                    {t("subscriptions.manage.inactive")}
                </p>
            )}

            {actionError && (
                <p className="text-sm text-red-600" role="alert">{actionError}</p>
            )}

            {actions.canWithdraw && (
                // Kept apart from the cancellation (§ 356a(1) BGB: the two
                // must not be confused).
                <div className="flex flex-col gap-3 border-t border-foreground/10 pt-6">
                    <p className="text-sm text-foreground">
                        {t("subscriptions.manage.withdraw_hint", {
                            date: formatDate(subscription.withdrawableUntil, stationZone),
                        })}
                    </p>
                    <Button
                        variant="outline"
                        disabled={isSubmitting}
                        onClick={() => {
                            setActionError(null);
                            setConfirmingWithdrawal(true);
                        }}
                    >
                        {t("subscriptions.withdraw_button")}
                    </Button>
                </div>
            )}
        </div>
    );
}

/**
 * Subscription Manage Page
 *
 * § 312k BGB: cancel — and inside the withdrawal period, withdraw from — a
 * kolula KEY subscription from any device. Reached from Infos ("Verträge hier
 * kündigen") through the existing login → verify pages with `intent=manage`.
 * The agreement id comes from Redux only; on a refresh without it the login
 * starts again (plan `kolula-key-subscription-b2c-obligations.md`, PR 5).
 */
const SubscriptionManagePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const customerAgreementId = useSelector(
        (state: RootState) => state.subscription.existingSubscription.customerAgreementId
    );
    const isVerified = useSelector(
        (state: RootState) => state.subscription.existingSubscription.isVerified
    );

    if (!customerAgreementId || !isVerified) {
        return <Navigate to={loginTarget} replace />;
    }

    return (
        <KeyHandoverPageLayout
            headerText={t("subscriptions.manage.header_text")}
            hideBackButton
            bottomActions={
                <Button size="lg" variant="outline" className="w-full" onClick={() => navigate(routes.orders)}>
                    {t("subscriptions.manage.to_bookings")}
                </Button>
            }
        >
            <KeyHandoverContentWrapper>
                <ManageContent subscriptionId={customerAgreementId} />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default SubscriptionManagePage;
